import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Container,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Star as StarIcon,
  Translate as TranslateIcon,
  VolumeUp as VolumeIcon,
  Mic as MicIcon,
  Chat as ChatIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  TrendingUp as TrendingIcon,
  Public as GlobalIcon
} from '@mui/icons-material';

const Detail = ({ description, description2, description3 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Intersection Observer for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const contentSections = [
    {
      icon: <StarIcon />,
      title: 'Our Mission',
      description: description,
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, rgba(245,158,11, 0.08) 0%, rgba(124, 58, 237, 0.04) 100%)'
    },
    {
      icon: <TrendingIcon />,
      title: 'Our Expertise',
      description: description2,
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, rgba(245,158,11, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%)'
    },
    {
      icon: <GlobalIcon />,
      title: 'Our Impact',
      description: description3,
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, rgba(245,158,11, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%)',
      isHighlighted: true
    }
  ];

  const highlights = [
    {
      icon: <TranslateIcon />,
      label: '5+ Languages',
      color: '#F59E0B'
    },
    {
      icon: <VolumeIcon />,
      label: 'Voice AI',
      color: '#F59E0B'
    },
    {
      icon: <MicIcon />,
      label: 'Speech Recognition',
      color: '#F59E0B'
    },
    {
      icon: <ChatIcon />,
      label: 'AI Chatbots',
      color: '#F59E0B'
    },
    {
      icon: <BusinessIcon />,
      label: 'Strategic Partners',
      color: '#F59E0B'
    },
    {
      icon: <SchoolIcon />,
      label: 'Academic Excellence',
      color: '#F59E0B'
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ position: 'relative' }}>
      <Card 
        ref={cardRef}
        sx={{
          borderRadius: '24px',
          overflow: 'hidden',
          background: 'white',
          border: '1px solid rgba(245,158,11, 0.08)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 30px 60px rgba(245,158,11, 0.15)',
            borderColor: 'rgba(245,158,11, 0.2)',
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardContent sx={{ 
          p: { xs: 4, md: 6, lg: 8 },
        }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="ABOUT PHOSAI"
              sx={{
                backgroundColor: 'rgba(245,158,11, 0.1)',
                color: '#F59E0B',
                fontWeight: 700,
                fontSize: '0.8rem',
                mb: 3,
                px: 3,
                py: 1.5,
                borderRadius: '20px',
              }}
            />
            
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                mb: 4,
                color: '#111827',
              }}
            >
              World-Class AI Solutions
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.7,
                color: '#6B7280',
                maxWidth: 700,
                margin: '0 auto',
                fontWeight: 500,
              }}
            >
              We bridge linguistic barriers across Africa with cutting-edge AI technology, 
              making technology accessible to every community while preserving cultural authenticity.
            </Typography>
          </Box>

          {/* Content Grid */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {contentSections.map((section, index) => (
              <Grid item xs={12} lg={section.isHighlighted ? 12 : 6} key={index}>
                <Box sx={{
                  p: 4,
                  borderRadius: '20px',
                  background: section.gradient,
                  border: `1px solid ${section.color}20`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 15px 35px ${section.color}15`,
                  },
                  ...(section.isHighlighted && {
                    background: `linear-gradient(135deg, rgba(245,158,11, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)`,
                    border: '1px solid rgba(245,158,11, 0.25)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${section.color}15 0%, transparent 70%)`,
                      zIndex: 0,
                    }
                  })
                }}>
                  {/* Section Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: '16px',
                      backgroundColor: `${section.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1) rotate(5deg)',
                      }
                    }}>
                      <Box sx={{ color: section.color, fontSize: 24 }}>
                        {section.icon}
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        color: section.color, 
                        fontWeight: 800,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Box>
                  
                  {/* Section Content */}
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#374151',
                      lineHeight: 1.8,
                      fontSize: { xs: '1rem', md: '1.05rem' },
                      fontWeight: 500,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {section.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Highlights Section */}
          <Box sx={{
            p: 4,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(245,158,11, 0.05) 0%, rgba(124, 58, 237, 0.02) 100%)',
            border: '1px solid rgba(245,158,11, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -100,
              right: -100,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245,158,11, 0.1) 0%, transparent 70%)',
              zIndex: 0,
            }
          }}>
            <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  color: '#111827',
                  mb: 2,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                Key Highlights
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#6B7280',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                Our comprehensive capabilities and achievements
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
              {highlights.map((highlight, index) => (
                <Grid item xs={6} sm={4} md={2} key={index}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${highlight.color}20`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${highlight.color}20`,
                      background: 'white',
                    }
                  }}>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: `${highlight.color}15`,
                      color: highlight.color,
                      mb: 2,
                    }}>
                      <Box sx={{ fontSize: 24 }}>
                        {highlight.icon}
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {highlight.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Detail;
